import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import {
  LayoutDashboard, Users, Target, ClipboardList, CheckSquare, Contact2,
  BarChart3, Bell, ScrollText, LogOut, Moon, Sun, KeyRound, Menu, X, Activity,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { initials, ROLE_LABEL } from "@/lib/format";
import api from "@/lib/api";
import { logger } from "@/lib/logger";

const ALL_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "manager", "team_leader", "sales_executive"] },
  { to: "/performance", label: "Performance", icon: Activity, roles: ["admin", "manager", "team_leader", "sales_executive"] },
  { to: "/users", label: "Users", icon: Users, roles: ["admin", "manager", "team_leader"] },
  { to: "/targets", label: "Targets", icon: Target, roles: ["admin", "manager", "team_leader", "sales_executive"] },
  { to: "/submissions", label: "Submissions", icon: ClipboardList, roles: ["admin", "manager", "team_leader", "sales_executive"] },
  // Approvals is visible to admins always, and to managers only when the admin has
  // enabled their `can_approve_submissions` permission. The backend enforces the
  // real permission on every action; this just hides the nav item when unusable.
  {
    to: "/approvals", label: "Approvals", icon: CheckSquare, roles: ["admin", "manager"],
    when: (u) => u.role === "admin" || !!u.can_approve_submissions,
  },
  { to: "/clients", label: "Clients", icon: Contact2, roles: ["admin", "manager", "team_leader", "sales_executive"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "manager", "team_leader", "sales_executive"] },
  { to: "/notifications", label: "Notifications", icon: Bell, roles: ["admin", "manager", "team_leader", "sales_executive"] },
  { to: "/audit", label: "Audit Logs", icon: ScrollText, roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let m = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (m) setUnread(data.filter((n) => !n.read).length);
      } catch (err) {
        // Silent — polling; log for observability
        if (m) logger.debug("notifications poll failed", err?.message || err);
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { m = false; clearInterval(id); };
  }, []);

  const nav = ALL_NAV.filter((i) =>
    i.roles.includes(user.role) && (typeof i.when !== "function" || i.when(user))
  );

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Mobile top bar */}
      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-border bg-background/80 px-4 py-3 backdrop-blur-xl lg:hidden">
        <div className="flex items-center gap-2">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="rounded-md p-2 hover:bg-muted"
            aria-label="Open menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <span className="font-display text-lg font-bold tracking-tight">Sales CRM</span>
        </div>
        <button
          onClick={toggle}
          aria-label="Toggle theme"
          className="rounded-md p-2 hover:bg-muted"
          data-testid="theme-toggle-mobile"
        >
          {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-border bg-card transition-transform lg:sticky lg:top-0 lg:h-screen lg:translate-x-0",
            open ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <div className="flex h-16 items-center gap-2 border-b border-border px-6">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground font-display font-bold">
              R
            </div>
            <div>
              <div className="font-display text-lg font-bold leading-none tracking-tight">Sales CRM</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Sales Command</div>
            </div>
          </div>

          <nav className="flex-1 space-y-0.5 overflow-y-auto p-3">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.to.slice(1)}`}
                className={({ isActive }) =>
                  cn(
                    "group flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )
                }
              >
                <span className="flex items-center gap-3">
                  <item.icon className="h-4 w-4" strokeWidth={1.75} />
                  {item.label}
                </span>
                {item.to === "/notifications" && unread > 0 && (
                  <span className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground tabular-nums">
                    {unread}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="border-t border-border p-3">
            <div className="flex items-center gap-3 rounded-md p-2">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                  {initials(user.name)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{user.name}</div>
                <div className="truncate text-xs text-muted-foreground">{ROLE_LABEL[user.role]}</div>
              </div>
            </div>
            <div className="mt-2 flex gap-1">
              <Button
                data-testid="change-password-btn"
                variant="ghost"
                size="sm"
                className="flex-1 justify-start text-xs"
                onClick={() => navigate("/change-password")}
              >
                <KeyRound className="mr-2 h-3.5 w-3.5" /> Password
              </Button>
              <Button
                data-testid="theme-toggle"
                variant="ghost"
                size="icon"
                onClick={toggle}
                aria-label="Toggle theme"
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="icon"
                onClick={doLogout}
                aria-label="Log out"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </aside>

        {/* Overlay */}
        {open && (
          <button
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          />
        )}

        <main className="min-h-screen flex-1 px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
          {children}
        </main>
      </div>
    </div>
  );
}
